<template>
  <aha-chat>
    <aha-chat-toggle
      @toggleChatBox="onToggleChatBox"
      :chatBoxVisible="isOpenChatBox"
      :hasChatNotification="hasChatNotification"
      :isDarkTheme="slideActive.textColour === '#ffffff'"
    >
      <aha-chat-box
        @sendMessage="handleSendMessageFromPresenter"
        @retrySendingMessage="retry"
        @focusChatInput="onFocusChatInput"
        @blurChatInput="onBlurChatInput"
        :totalLiveParticipants="totalLiveParticipants"
        :chatMessages="chatMessages"
        width="380px"
        chatsHeight="185px"
        :isDarkTheme="slideActive.textColour === '#ffffff'"
        class="chat-box__custom"
        :filteringProfanity="!!presentation.filteringProfanity"
        :emptyContentHTML="emptyContentHTML"
        :userId="`${currentUser.id}`"
      >
      </aha-chat-box>
    </aha-chat-toggle>
  </aha-chat>
</template>

<script>
import { mapActions, mapState, mapGetters } from 'vuex'
import Socket from '@/socket'
import Util from '@/util'
import eventCategories from '@/constant/eventCategories'
import { getMessagesLocally } from '@/vuex/modules/presentation/chat.module'

export default {
  props: {
    openByDefault: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    totalLiveParticipants: 100, // for v2 of chat
  }),

  computed: {
    ...mapState({
      presentation: (state) => state.presentation,
      currentUser: (state) => state.currentUser,
      slideActive: (state) => state.slideActive,
    }),
    ...mapGetters('presentationChat', ['chatMessages', 'isOpenChatBox', 'hasChatNotification']),

    emptyContentHTML() {
      return `<p class="chat-box__empty-text"/>${this.$t('liveMsgShownHere')}</p>`
    },
  },

  mounted() {
    // get messages from audience
    this.subscribeChatRoomAction({
      presentation: this.presentation,
      newMessageHandler: this.handleGetMessages,
    })
    this.toggleChatBoxAction({
      isOpen: this.openByDefault,
    })
    const chatInLocal = getMessagesLocally(this.presentation.uniqueAccessCode)
    if (chatInLocal.length === 0) {
      this.showChatNotificationAction({
        isShowing: false,
      })
    }
  },

  destroyed() {
    this.unsubscribeChatRoomAction({
      presentation: this.presentation,
    })
  },

  methods: {
    ...mapActions('presentationChat', [
      'subscribeChatRoomAction',
      'unsubscribeChatRoomAction',
      'sendMessageAction',
      'toggleChatBoxAction',
      'toggleFocusChatInputAction',
      'showChatNotificationAction',
    ]),

    handleGetMessages(newMessage) {
      if (newMessage.uniqueAccessCode && !this.isOpenChatBox) {
        this.showChatNotificationAction({
          isShowing: true,
        })
      }
    },

    handleSendMessageFromPresenter(message) {
      const {
        firstName, lastName, avatar, id,
      } = this.currentUser

      const payload = {
        type: 'text',
        presentationId: this.presentation.id,
        uniqueAccessCode: this.presentation.uniqueAccessCode,
        message,
        socketId: Socket.getSocketId(),
        timestamp: Date.now(),
        userId: id,
        name: `${firstName} ${lastName}`,
        avatar,
        role: 'presenter',
      }

      this.sendMessageAction({
        payload,
      })
    },

    onToggleChatBox() {
      this.toggleChatBoxAction()
      if (this.isOpenChatBox && this.hasChatNotification) {
        this.showChatNotificationAction({
          isShowing: false,
        })
      }
      const gaText = this.isOpenChatBox ? 'Open' : 'Close'
      Util.mixpanelTracking(
        eventCategories.PRESENTING_MODE,
        'Chat in Lobby > Toggle chat box',
        `${this.presentation.id} > Chat > Presenter > Toggle chatbox > ${gaText}`,
      )
    },

    onFocusChatInput() {
      this.toggleFocusChatInputAction({
        isFocus: true,
      })
    },

    onBlurChatInput() {
      this.toggleFocusChatInputAction({
        isFocus: false,
      })
    },

    retry(message) {
      this.handleSendMessageFromPresenter(message)
    },
  },
}
</script>

<style lang="scss" scoped>
.chat-box__custom {
  @apply absolute;
  left: -12rem;
  bottom: 3.5rem;
  box-shadow: 0px 6px 11px rgba(0, 0, 0, 0.07);
  backdrop-filter: blur(6px);
  &::v-deep {
    .chat-box__header {
      top: -1px;
    }
  }
  &::v-deep .chat-message {
    .__user-type-icon {
      margin-bottom: 4px;
    }
    .__avatar {
      margin-right: 8px;
    }

    & .chat-message.__name {
      & span:first-child {
        @apply inline-block max-w-52 align-bottom truncate
      }
    }
  }
}
</style>
